import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  Routes,
} from '@angular/router';
import { inject } from '@angular/core';
import { AuthService } from './features/auth/auth.service';
import { CookieService } from 'ngx-cookie-service';
import { EnvService } from './core';
import { langUrlMatcher } from './core/localize-router/localize-router.service';

const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const authService = inject(AuthService);
  const cookieService = inject(CookieService);
  if (authService.userData.user.userId) {
    return true;
  }

  // Store the attempted URL for redirecting
  authService.redirectUrl = state.url;

  // Navigate to the login page with extras
  if (!cookieService.get(authService.tokenName)) {
    return inject(Router).createUrlTree(['/', 'home']);
  }
  return false;
};

const ltlGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const authService = inject(AuthService);
  const envService = inject(EnvService);

  if (
    authService.userData.user.userId &&
    authService?.userData?.optionals?.isLTLCustomer &&
    envService.showLtlRatingFeature
  ) {
    return true;
  } else {
    return inject(Router).createUrlTree(['/', 'home']);
  }
};

const tlGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const authService = inject(AuthService);
  const envService = inject(EnvService);

  if (
    authService.userData.user.userId &&
    authService?.userData?.optionals?.isTLCustomer &&
    envService.showTlQuickQuoteFeature
  ) {
    return true;
  } else {
    return inject(Router).createUrlTree(['/', 'home']);
  }
};

const kpiGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const authService = inject(AuthService);

  if (
    authService.userData.user.userId &&
    authService.userData.optionals?.showKpiDashboard
  ) {
    return true;
  } else {
    return inject(Router).createUrlTree(['/', 'home']);
  }
};

export const APP_ROUTES: Routes = [
  {
    path: '',
    redirectTo: 'home',
    data: { titleRef: 'SHARED.PAGE-TITLES.HOME' },
    pathMatch: 'full',
  },
  {
    path: 'warehouse',
    canActivate: [authGuard],
    data: { titleRef: 'SHARED.PAGE-TITLES.WAREHOUSE' },
    loadComponent: () =>
      import('./features/warehouse/warehouse.component').then(
        m => m.WarehouseComponent
      ),
  },
  {
    path: 'track-po',
    data: { titleRef: 'SHARED.PAGE-TITLES.TRACK-PO' },
    loadComponent: () =>
      import('./features/track-po/track-po.component').then(
        m => m.TrackPoComponent
      ),
  },
  {
    path: 'tl',
    canActivate: [authGuard, tlGuard],
    children: [
      {
        path: 'quick-quote',
        data: { titleRef: 'SHARED.PAGE-TITLES.TL-QUICK-QUOTE' },
        loadComponent: () =>
          import('./features/tl/quick-quote/quick-quote.component').then(
            m => m.QuickQuoteComponent
          ),
      },
    ],
  },
  {
    path: 'rate-portal',
    redirectTo: 'tl/quick-quote',
    pathMatch: 'full',
  },
  {
    path: 'home',
    data: { titleRef: 'SHARED.PAGE-TITLES.HOME' },
    loadComponent: () =>
      import('./features/home/home.component').then(m => m.HomeComponent),
  },
  {
    path: 'footer',
    children: [
      {
        path: 'faq',
        data: { titleRef: 'SHARED.PAGE-TITLES.FAQ' },
        loadComponent: () =>
          import('./features/footer/faq/faq.component').then(
            m => m.FaqComponent
          ),
      },
      {
        path: 'what-new',
        canActivate: [authGuard],
        data: { titleRef: 'SHARED.PAGE-TITLES.WHAT-NEW' },
        loadComponent: () =>
          import('./features/footer/what-new/what-new.component').then(
            m => m.WhatNewComponent
          ),
      },
      {
        path: 'terms-and-conditions',
        data: { titleRef: 'SHARED.PAGE-TITLES.TERMS-AND-CONDITIONS' },
        loadComponent: () =>
          import(
            './features/footer/terms-and-conditions/terms-and-conditions.component'
          ).then(m => m.TermsAndConditionsComponent),
      },
      {
        path: 'feedback',
        data: { titleRef: 'SHARED.PAGE-TITLES.FEEDBACK' },
        loadComponent: () =>
          import('./features/footer/feedback/feedback.component').then(
            m => m.FeedbackComponent
          ),
      },
    ],
  },
  {
    path: 'header',
    loadComponent: () =>
      import('./features/header/header.component').then(m => m.HeaderComponent),
  },
  {
    path: 'tracking',
    canActivate: [authGuard],
    data: { titleRef: 'SHARED.PAGE-TITLES.TRACKING' },

    loadComponent: () =>
      import('./features/tracking/tracking.component').then(
        m => m.TrackingComponent
      ),
  },
  {
    path: 'powerbi',
    canActivate: [authGuard],
    data: { titleRef: 'HEADER.LBL_NAV-REPORTS' },

    loadComponent: () =>
      import('./features/power-bi/power-bi.component').then(
        m => m.PowerBIComponent
      ),
  },
  {
    path: 'kpi-dashboard',
    canActivate: [authGuard, kpiGuard],
    data: { titleRef: 'SHARED.PAGE-TITLES.KPI-DASHBOARD' },

    loadComponent: () =>
      import('./features/kpi-dashboard/kpi-dashboard.component').then(
        m => m.KpiDashboardComponent
      ),
  },
  {
    path: 'air-ocean',
    canActivate: [authGuard],
    data: { titleRef: 'SHARED.PAGE-TITLES.AIR-OCEAN' },
    loadComponent: () =>
      import('./features/air-ocean/air-ocean.component').then(
        m => m.AirOceanComponent
      ),
  },
  {
    path: 'contact-us',
    canActivate: [authGuard],
    data: { titleRef: 'SHARED.PAGE-TITLES.CONTACT-US' },
    loadComponent: () =>
      import('./features/auth/contact-us/contact-us.component').then(
        mod => mod.ContactUsComponent
      ),
  },
  {
    path: 'profile',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/auth/profile/profile.component').then(
        mod => mod.ProfileComponent
      ),
    data: { titleRef: 'SHARED.PAGE-TITLES.PROFILE' },
  },
  {
    path: 'ltl',
    canActivate: [authGuard, ltlGuard],
    children: [
      {
        path: 'book',
        data: { titleRef: 'SHARED.PAGE-TITLES.LTL-BOOKING' },
        loadComponent: () =>
          import('./features/ltl/booking/booking.component').then(
            m => m.BookingComponent
          ),
      },
    ],
  },
  {
    path: 'error',
    data: { titleRef: 'SHARED.PAGE-TITLES.ERROR', code: 500 },
    loadComponent: () =>
      import('./shared/components/error-handler/error-handler.component').then(
        mod => mod.ErrorHandlerComponent
      ),
  },
  {
    path: 'sso',
    data: { titleRef: 'SHARED.PAGE-TITLES.REDIRECTION' },
    loadComponent: () =>
      import('./shared/components/sso/sso.component').then(
        mod => mod.SsoComponent
      ),
  },
];

export const LOCALIZED_APP_ROUTES: Routes = [
  {
    matcher: langUrlMatcher,
    children: APP_ROUTES,
  },
];
